import { fontFamily, typographyScale } from '../primitives/typography';

const pxToRem = (px: number) => `${px / 16}rem`;

const lineHeightRatio = (lineHeight: number, fontSize: number) => String(lineHeight / fontSize);

type TypographyStyle = {
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
};

const toTextStyleValue = (style: TypographyStyle) => ({
  fontSize: pxToRem(style.fontSize),
  fontWeight: style.fontWeight,
  lineHeight: lineHeightRatio(style.lineHeight, style.fontSize),
});

/** Chakra typography tokens — derived from KDS typography scale. */
export const typography = {
  fonts: {
    heading: { value: `'${fontFamily}', sans-serif` },
    body: { value: `'${fontFamily}', sans-serif` },
  },
  fontSizes: {
    /** Legacy app token (10px) — not in KDS scale; used by map labels, nexus tags, etc. */
    xxs: { value: pxToRem(10) },
    xs: { value: pxToRem(typographyScale.small.regular.fontSize) },
    sm: { value: pxToRem(typographyScale.default.regular.fontSize) },
    md: { value: pxToRem(typographyScale.large.regular.fontSize) },
    lg: { value: pxToRem(typographyScale.h5.regular.fontSize) },
    xl: { value: pxToRem(typographyScale.h4.regular.fontSize) },
    '2xl': { value: pxToRem(typographyScale.h3.bold.fontSize) },
    '3xl': { value: pxToRem(typographyScale.h2.bold.fontSize) },
    '4xl': { value: pxToRem(typographyScale.h1.bold.fontSize) },
  },
  fontWeights: {
    normal: { value: 400 },
    medium: { value: 500 },
    bold: { value: 700 },
  },
  lineHeights: {
    h1: { value: lineHeightRatio(typographyScale.h1.bold.lineHeight, typographyScale.h1.bold.fontSize) },
    h2: { value: lineHeightRatio(typographyScale.h2.bold.lineHeight, typographyScale.h2.bold.fontSize) },
    h3: { value: lineHeightRatio(typographyScale.h3.bold.lineHeight, typographyScale.h3.bold.fontSize) },
    h4: { value: lineHeightRatio(typographyScale.h4.regular.lineHeight, typographyScale.h4.regular.fontSize) },
    h5: { value: lineHeightRatio(typographyScale.h5.regular.lineHeight, typographyScale.h5.regular.fontSize) },
    default: {
      value: lineHeightRatio(typographyScale.default.regular.lineHeight, typographyScale.default.regular.fontSize),
    },
    small: {
      value: lineHeightRatio(typographyScale.small.regular.lineHeight, typographyScale.small.regular.fontSize),
    },
  },
};

/**
 * KDS text styles — keys match Figma scale steps: `{scale}.{weight}` (e.g. `default.regular`, `small.medium`).
 * Generated from typographyScale so token names stay aligned with Foundations.
 */
export const textStyles = Object.fromEntries(
  Object.entries(typographyScale).flatMap(([scale, variants]) =>
    Object.entries(variants).map(([weight, style]) => [`${scale}.${weight}`, { value: toTextStyleValue(style) }])
  )
);

/** All KDS textStyle token names, in Figma scale order. */
export const textStyleTokens = Object.entries(typographyScale).flatMap(([scale, variants]) =>
  Object.keys(variants).map(weight => `${scale}.${weight}`)
);

type TypographyScale = typeof typographyScale;
export type TextStyleToken = {
  [S in keyof TypographyScale]: `${S & string}.${Extract<keyof TypographyScale[S], string>}`;
}[keyof TypographyScale];
